import React, { useState, useEffect } from 'react';
import './ImportPreview.css';

function ImportPreview({ preview, onConfirm, onCancel, isImporting }) {
  const [selectedContacts, setSelectedContacts] = useState(
    new Set(preview.contacts.map((_, i) => i))
  );

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const toggleContact = (index) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedContacts(newSelected);
  };

  const toggleAll = () => {
    if (selectedContacts.size === preview.contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(preview.contacts.map((_, i) => i)));
    }
  };

  const handleConfirm = () => {
    const contactsToImport = preview.contacts.filter((_, i) => selectedContacts.has(i));
    onConfirm(contactsToImport);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="import-preview-overlay">
      <div className="import-preview-modal">
        <div className="import-preview-header">
          <h2>Import Preview</h2>
          <button className="import-preview-close" onClick={onCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="import-preview-meta">
          <div className="meta-item">
            <span className="meta-label">File</span>
            <span className="meta-value">{preview.fileName}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Size</span>
            <span className="meta-value">{formatFileSize(preview.fileSize)}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Contacts Found</span>
            <span className="meta-value highlight">{preview.totalFound || preview.contacts.length}</span>
          </div>
        </div>

        {preview.columns && preview.columns.length > 0 && (
          <div className="import-preview-columns">
            <h3>Column Mapping</h3>
            <div className="column-mappings">
              {preview.columns.map((col, i) => (
                <div key={i} className="column-mapping">
                  <span className="original-col">{col.original}</span>
                  <span className="mapping-arrow">→</span>
                  <span className={`mapped-col ${col.mapped}`}>{col.mapped}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {preview.warnings && preview.warnings.length > 0 && (
          <div className="import-preview-warnings">
            {preview.warnings.map((warning, i) => (
              <div key={i} className="warning-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4m0 4h.01M12 2L2 22h20L12 2z" />
                </svg>
                {warning}
              </div>
            ))}
          </div>
        )}

        <div className="import-preview-table-header">
          <h3>Contacts to Import</h3>
          <label className="select-all-toggle">
            <input
              type="checkbox"
              checked={selectedContacts.size === preview.contacts.length}
              onChange={toggleAll}
            />
            Select All ({selectedContacts.size}/{preview.contacts.length})
          </label>
        </div>

        <div className="import-preview-table-container">
          <table className="import-preview-table">
            <thead>
              <tr>
                <th className="col-select"></th>
                <th className="col-name">Name</th>
                <th className="col-amount">Amount</th>
                <th className="col-email">Email</th>
                <th className="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {preview.contacts.map((contact, index) => (
                <tr
                  key={index}
                  className={selectedContacts.has(index) ? 'selected' : 'deselected'}
                  onClick={() => toggleContact(index)}
                >
                  <td className="col-select">
                    <input
                      type="checkbox"
                      checked={selectedContacts.has(index)}
                      onChange={() => toggleContact(index)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="col-name">{contact.name}</td>
                  <td className="col-amount">{contact.amount || '-'}</td>
                  <td className="col-email">{contact.email || '-'}</td>
                  <td className="col-notes">
                    {contact.notes ? (
                      contact.notes.length > 60
                        ? contact.notes.substring(0, 60) + '...'
                        : contact.notes
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {preview.truncated && (
          <div className="import-preview-truncated">
            Showing first {preview.contacts.length} of {preview.totalFound} contacts. All will be imported.
          </div>
        )}

        <div className="import-preview-actions">
          <button className="cancel-btn" onClick={onCancel} disabled={isImporting}>
            Cancel
          </button>
          <button
            className="confirm-btn"
            onClick={handleConfirm}
            disabled={selectedContacts.size === 0 || isImporting}
          >
            {isImporting ? (
              <>
                <span className="importing-spinner"></span>
                Importing...
              </>
            ) : (
              `Import ${selectedContacts.size} Contact${selectedContacts.size !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportPreview;
